"use client";

import { Suspense, useState } from "react";
import { motion } from "framer-motion";
import dynamic from "next/dynamic";

const DashboardVoxel = dynamic(() => import("./DashboardVoxel"), { ssr: false });

const benefits = [
  { title: "Zero setup", description: "Keep the tab open. Your browser becomes a relay node automatically." },
  { title: "Passive income", description: "Earn a percentage of every task routed through your node." },
  { title: "Scale with uptime", description: "Longer uptime means higher trust, more routing, more earnings." },
];

export default function EarnRelay() {
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);

  return (
    <section className="py-[80px] md:py-[140px] px-6 bg-bg" id="relay">
      <div className="max-w-[1200px] mx-auto">
        {/* TOP ROW — full width header */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-[14px] text-text-muted uppercase tracking-[2px] mb-3" style={{ fontFamily: "var(--font-body)" }}>Earn</p>
          <h2 className="text-[clamp(1.75rem,3.5vw,44px)] font-extrabold text-cream leading-[1.1] tracking-[-0.02em]" style={{ fontFamily: "var(--font-heading)" }}>
            Run a relay node. Earn from every task on the network.
          </h2>
          <p className="mt-4 text-[17px] leading-[1.7] max-w-[600px]" style={{ fontFamily: "var(--font-body)", color: "rgba(242,240,237,0.6)" }}>
            Your device becomes part of the network. Route tasks, verify results, earn a cut of every transaction.
          </p>
        </motion.div>

        {/* BOTTOM ROW — bullets LEFT, dashboard RIGHT */}
        <div className="relay-layout" style={{ display: "flex", alignItems: "center", gap: "60px", marginTop: "48px" }}>
          {/* Left — Three bullet points */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relay-bullets-col"
            style={{ flex: "0 0 36%", display: "flex", flexDirection: "column", justifyContent: "center", gap: "8px", overflow: "visible" }}
          >
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}
                style={{ position: "relative", minHeight: "110px", cursor: "default" }}
                onMouseEnter={() => setHoveredItem(i)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                {/* Giant decorative number */}
                <div className="relay-bg-number" style={{
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  transform: "translateY(-50%)",
                  fontSize: "130px",
                  fontWeight: 800,
                  fontFamily: "var(--font-heading)",
                  color: "#F6AE2D",
                  opacity: hoveredItem === i ? 0.25 : 0.18,
                  lineHeight: 1,
                  pointerEvents: "none",
                  zIndex: 0,
                  userSelect: "none",
                  transition: "opacity 0.2s ease",
                  letterSpacing: "-0.04em",
                }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                {/* Text overlapping the number */}
                <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", justifyContent: "center", minHeight: "110px", marginLeft: "60px" }}>
                  <h3 style={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: "22px", color: "#F2F0ED", lineHeight: 1.2 }}>{b.title}</h3>
                  <p style={{ fontFamily: "var(--font-body)", fontWeight: 400, fontSize: "16px", color: "rgba(242,240,237,0.7)", lineHeight: 1.6, marginTop: "6px", maxWidth: "350px" }}>{b.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Right — Voxel Dashboard (shifted 20% right) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relay-visual-col"
            style={{ flex: "1 1 auto", overflow: "visible", marginLeft: "4%" }}
          >
            <Suspense fallback={<div style={{ width: "100%", height: 500 }} />}>
              <DashboardVoxel />
            </Suspense>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
